import _ from 'lodash';
// The distance sub-module only: `natural`'s root entry pulls in its taggers and classifiers, which
// read from disk and cannot be bundled for the browser — and the review UI runs this matcher too.
import { JaroWinklerDistance, LevenshteinDistance } from 'natural/lib/natural/distance';

export type MatchConfig = {
  maxInputLength: number;
  minScore: number;
  autoselectThreshold: number;
  autoselectGapThreshold: number;
  maxCandidates: number;
  jaroWinklerWeight: number;
  levenshteinWeight: number;
  tokenSimilarityWeight: number;
  houseNumberWeight: number;
  zipWeight: number;
  textConflictPenalty: number;
};

export const DEFAULT_MATCH_CONFIG: MatchConfig = {
  maxInputLength: 1000,
  minScore: 0.6,
  autoselectThreshold: 0.9,
  autoselectGapThreshold: 0.01,
  maxCandidates: 30,
  jaroWinklerWeight: 0.7,
  levenshteinWeight: 0.3,
  tokenSimilarityWeight: 0.9,
  houseNumberWeight: 0.2,
  zipWeight: 0.2,
  textConflictPenalty: 0.15,
};

type SimilarityWeights = {
  jaroWinklerWeight: number;
  levenshteinWeight: number;
  tokenSimilarityWeight: number;
  maxInputLength: number;
};

type MatchAddressResult<AddressType extends { address: string }> = (AddressType & {
  score: number;
  isAutoAssigned: boolean;
})[];

export class BuildingAddressMatcherCore {
  private normalize(text: string, maxInputLength: number): string {
    if (!text) return '';
    const s = (text.length > maxInputLength ? text.substring(0, maxInputLength) : text)
      .toLowerCase()
      .replace(/\[[^\]]*?\]/g, ' ')
      .replace(/\([^)]*?\)/g, ' ')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/str\./g, 'strasse')
      .replace(/pl\./g, 'platz')
      .replace(/st\./g, 'sankt')
      .replace(/dr\./g, 'doktor')
      .replace(/\bstrasse\b/g, 'strasse')
      .replace(/\bstr\b/g, 'strasse')
      .replace(/\bplatz\b/g, 'platz')
      .replace(/\bpl\b/g, 'platz')
      .replace(/\bsankt\b/g, 'sankt')
      .replace(/\bst\b/g, 'sankt')
      .replace(/\bdoktor\b/g, 'doktor')
      .replace(/\bdr\b/g, 'doktor')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return s;
  }

  private extractZip(text: string): string | undefined {
    return text.match(/\b\d{5}\b/)?.[0];
  }

  private extractAddressTokens(text: string, maxInputLength: number): Set<string> {
    const normalized = this.normalize(text, maxInputLength);
    return new Set(normalized.split(/\s+/).filter(Boolean));
  }

  /**
   * Extracts house numbers (23a) and expands ranges (e.g. "1-3" -> {1, 2, 3}).
   */
  private extractHouseNumbers(text: string, maxCandidates: number): Set<string> {
    const rangeNumbers = Array.from(text.matchAll(/\b(\d{1,4})\s*-\s*(\d{1,4})\b/g)).flatMap((match) => {
      const start = Number.parseInt(match[1], 10);
      const end = Number.parseInt(match[2], 10);

      return start < end && end - start < maxCandidates
        ? Array.from({ length: end - start + 1 }, (_, i) => (start + i).toString())
        : [start.toString(), end.toString()];
    });

    const listNumbers = Array.from(text.matchAll(/\b(\d{1,4})\s*[/+]\s*(\d{1,4})\b/g)).flatMap((match) => [
      match[1],
      match[2],
    ]);

    const singleNumbers = Array.from(text.matchAll(/\b(\d{1,4})\s*([a-zA-Z])?\b/g)).map((match) => {
      const num = match[1];
      const suffix = match[2] ? match[2].toLowerCase() : '';
      return `${num}${suffix}`;
    });

    return new Set([...rangeNumbers, ...listNumbers, ...singleNumbers]);
  }

  /**
   * Check if text tokens fuzzy match.
   */
  private isTextSubset(tokensA: Set<string>, tokensB: Set<string>, tokenSimilarityWeight: number): boolean {
    if (tokensA.size === 0) return true;

    const isMeaningfulText = (t: string) => Number.isNaN(Number(t)) && t.length > 1;
    const textTokensA = [...tokensA].filter(isMeaningfulText);
    const textTokensB = [...tokensB].filter(isMeaningfulText);

    if (textTokensA.length === 0) return true;
    if (textTokensB.length === 0) return false;

    return textTokensA.every((tokenA) =>
      textTokensB.some((tokenB) => JaroWinklerDistance(tokenA, tokenB) >= tokenSimilarityWeight),
    );
  }

  private similarity(a: string, b: string, weights: SimilarityWeights): number {
    if (!a || !b) return 0;
    if (a === b) return 1;

    const { jaroWinklerWeight, levenshteinWeight, tokenSimilarityWeight, maxInputLength } = weights;

    // String-based similarity
    const jaro = JaroWinklerDistance(a, b);
    const lev = 1 - LevenshteinDistance(a, b) / Math.max(a.length, b.length);
    const stringSimilarity = jaroWinklerWeight * jaro + levenshteinWeight * lev;

    // Token-based similarity (fuzzy). Pure-numeric tokens (house numbers, ZIPs) are excluded here.
    const isNumericToken = (t: string) => /^\d+$/.test(t);
    const tokensA = [...this.extractAddressTokens(a, maxInputLength)].filter((t) => !isNumericToken(t));
    const tokensB = [...this.extractAddressTokens(b, maxInputLength)].filter((t) => !isNumericToken(t));
    const matchedBIndexes = new Set<number>();

    for (const tokenA of tokensA) {
      const bestMatch = tokensB.reduce(
        (best, tokenB, index) => {
          if (matchedBIndexes.has(index)) return best;

          const score = JaroWinklerDistance(tokenA, tokenB);
          return score > best.score ? { index, score } : best;
        },
        { index: -1, score: 0 },
      );

      if (bestMatch.score >= tokenSimilarityWeight) {
        matchedBIndexes.add(bestMatch.index);
      }
    }

    // Overlap coefficient (intersection / smaller set) rather than Jaccard (intersection / union):
    // the extracted input often carries extra context the candidate lacks
    const intersectionSize = matchedBIndexes.size;
    const smallerSize = Math.min(tokensA.length, tokensB.length);
    const tokenSimilarity = smallerSize === 0 ? 0 : intersectionSize / smallerSize;

    return Math.max(stringSimilarity, tokenSimilarity * tokenSimilarityWeight);
  }

  private calculateScoreModifier(
    hasSharedNumbers: boolean,
    isTextSubset: boolean,
    inputZip: string | undefined,
    candZip: string | undefined,
    config: MatchConfig,
  ): number {
    const hasZipConflict = !!inputZip && !!candZip && inputZip !== candZip;
    if (hasZipConflict) {
      return 1 - 2 * config.zipWeight;
    }

    if (!hasSharedNumbers) {
      return 1 - config.houseNumberWeight;
    }

    const textBonus = !isTextSubset ? -config.textConflictPenalty : config.houseNumberWeight;
    const zipBonus = inputZip && candZip ? config.zipWeight : 0;

    return 1 + textBonus + zipBonus;
  }

  private evaluateCandidateScore(
    candidateAddress: string,
    normExtractedAddress: string,
    inputTokens: Set<string>,
    inputNumbers: Set<string>,
    inputZip: string | undefined,
    config: MatchConfig,
  ): number {
    const candidateMatch = this.normalize(candidateAddress, config.maxInputLength);

    const baseScore = this.similarity(normExtractedAddress, candidateMatch, {
      jaroWinklerWeight: config.jaroWinklerWeight,
      levenshteinWeight: config.levenshteinWeight,
      tokenSimilarityWeight: config.tokenSimilarityWeight,
      maxInputLength: config.maxInputLength,
    });

    const candNumbers = this.extractHouseNumbers(candidateAddress, config.maxCandidates);
    const candTokens = this.extractAddressTokens(candidateAddress, config.maxInputLength);
    const candZip = this.extractZip(candidateMatch);

    const hasSharedNumbers = [...inputNumbers].some((n) => candNumbers.has(n));
    const isTextSubset = this.isTextSubset(inputTokens, candTokens, config.tokenSimilarityWeight);

    const scoreModifier = this.calculateScoreModifier(hasSharedNumbers, isTextSubset, inputZip, candZip, config);

    return Math.min(1, baseScore * scoreModifier);
  }

  private getAutoAssignmentEvaluator(
    rankedCandidates: Array<{ score: number }>,
    hasInputZip: boolean,
    config: MatchConfig,
  ): (score: number) => boolean {
    const isSingleMatch = rankedCandidates.length === 1;
    const topScore = rankedCandidates.length > 0 ? rankedCandidates[0].score : 0;
    const hasAnyAutoSelectedByScore = topScore >= config.autoselectThreshold;

    if (hasAnyAutoSelectedByScore) {
      const effectiveThreshold = Math.max(config.autoselectThreshold, topScore - config.autoselectGapThreshold);
      return (score: number) => score >= effectiveThreshold;
    }

    return () => !hasInputZip && isSingleMatch;
  }

  async matchAddress<AddressType extends { address: string }>(
    extractedAddress: string,
    matchableAdresses: AddressType[],
    config: MatchConfig,
  ): Promise<MatchAddressResult<AddressType>> {
    if (!extractedAddress) return [];

    // 1. Extract Input Features
    const normExtractedAddress = this.normalize(extractedAddress, config.maxInputLength);
    const inputTokens = this.extractAddressTokens(extractedAddress, config.maxInputLength);
    const inputNumbers = this.extractHouseNumbers(extractedAddress, config.maxCandidates);
    const inputZip = this.extractZip(normExtractedAddress);

    // 2. Score Candidates
    const candidates = matchableAdresses.map((addr) => ({
      ...addr,
      score: this.evaluateCandidateScore(
        addr.address,
        normExtractedAddress,
        inputTokens,
        inputNumbers,
        inputZip,
        config,
      ),
    }));

    // 3. Filter and Rank
    const ranked = _.sortBy(
      candidates.filter((c) => c.score >= config.minScore),
      (c) => -c.score,
    );

    // 4. Determine Auto-Assignment Rules
    const isAutoAssigned = this.getAutoAssignmentEvaluator(ranked, !!inputZip, config);

    // 5. Apply Rules and Return Target Count
    return ranked.slice(0, config.maxCandidates).map((c) => ({
      ...c,
      isAutoAssigned: isAutoAssigned(c.score),
    }));
  }
}
